import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Plus, Check, X, Trash2, Edit2, ChevronDown, Globe, Lock, Users, Sparkles, Image as ImageIcon, Pen } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GENRES = [
  "Family History", "Travel", "Career", "Childhood",
  "Love & Relationships", "Personal Growth", "Adventure",
  "Health & Wellness", "Spirituality", "Other"
];

const BookSelector = ({ books, activeBookId, onSwitch, onRefresh, writingStyle, onWritingStyleChange, onOpenOccasions, onRequestCreate, creationPreset, onCreationPresetConsumed, onAfterBookCreate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newGenre, setNewGenre] = useState("Personal Growth");
  const [newTemplate, setNewTemplate] = useState("freestyle");
  const [newCategory, setNewCategory] = useState("life");
  const [renamingId, setRenamingId] = useState(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [suggestingId, setSuggestingId] = useState(null);
  const [coverGenId, setCoverGenId] = useState(null);
  const inputRef = useRef(null);

  const activeBook = books.find(b => b.id === activeBookId) || books[0];

  // Auto-open + prefill creation form when parent pushes a preset (from StoryCategoryPicker)
  useEffect(() => {
    if (!creationPreset) return;
    setIsOpen(true);
    setCreating(true);
    setNewTitle(creationPreset.title || "");
    setNewGenre(creationPreset.genre || "Personal Growth");
    setNewTemplate(creationPreset.template || "freestyle");
    setNewCategory(creationPreset.category || "life");
    onCreationPresetConsumed?.();
  }, [creationPreset, onCreationPresetConsumed]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    try {
      const response = await axios.post(`${API}/books`, { title: newTitle.trim(), genre: newGenre, template: newTemplate });
      const createdCategory = newCategory;
      setNewTitle("");
      setNewGenre("Personal Growth");
      setNewTemplate("freestyle");
      setNewCategory("life");
      setCreating(false);
      await onRefresh();
      onSwitch(response.data.id);
      toast.success(`"${newTitle.trim()}" created!`);
      // Notify parent so it can set onboarding_mode for short stories
      onAfterBookCreate?.({ id: response.data.id, category: createdCategory });
    } catch (error) {
      toast.error("Failed to create book");
    }
  };

  const handleRename = async (bookId) => {
    if (!renameTitle.trim()) return;
    try {
      await axios.put(`${API}/books/${bookId}`, { title: renameTitle.trim() });
      setRenamingId(null);
      await onRefresh();
      toast.success("Book renamed");
    } catch (error) {
      toast.error("Failed to rename");
    }
  };

  const handleDelete = async (bookId) => {
    try {
      await axios.delete(`${API}/books/${bookId}`);
      setDeletingId(null);
      await onRefresh();
      toast.success("Book deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const handleSuggestTitle = async (e, bookId) => {
    e.stopPropagation();
    if (suggestingId) return;
    setSuggestingId(bookId);
    const toastId = toast.loading("Asking AI for a fresh title…");
    try {
      const res = await axios.post(`${API}/books/${bookId}/suggest-title`);
      await onRefresh();
      toast.success(`Renamed to "${res.data.title}"${res.data.genre ? ` (${res.data.genre})` : ""}!`, { id: toastId, duration: 6000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't suggest a title. Try again.", { id: toastId });
    } finally {
      setSuggestingId(null);
    }
  };

  const handleSuggestCover = async (e, bookId) => {
    e.stopPropagation();
    if (coverGenId) return;
    setCoverGenId(bookId);
    const toastId = toast.loading("Painting your book cover with AI… this takes ~20s");
    try {
      const res = await axios.post(`${API}/books/${bookId}/suggest-cover`);
      await onRefresh();
      toast.success("Your beautiful new cover is ready!", { id: toastId, duration: 5000 });
      // No-op for unused res; the cover_image now lives in book record and StoryCanvas will pick it up
      void res;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't generate a cover. Try again.", { id: toastId });
    } finally {
      setCoverGenId(null);
    }
  };

  const handleSwitch = async (bookId) => {
    try {
      await axios.put(`${API}/books/${bookId}/activate`);
      onSwitch(bookId);
      setIsOpen(false);
    } catch (error) {
      toast.error("Failed to switch book");
    }
  };

  const handleTogglePublic = async (e, bookId, currentlyPublic) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/books/${bookId}/public`, { is_public: !currentlyPublic });
      await onRefresh();
      toast.success(!currentlyPublic ? "Story is now public!" : "Story is now private");
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  return (
    <div className="relative" data-testid="book-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 rounded-xl bg-white/60 border border-[#D4C9B8]/50 hover:bg-white/80 transition-all max-w-[160px] sm:max-w-none"
        data-testid="book-selector-toggle"
      >
        <BookOpen className="w-4 h-4 text-[#7A9B7E] flex-shrink-0" />
        <span className="font-sans text-xs sm:text-sm font-medium text-[#2D3A2D] max-w-[80px] sm:max-w-[140px] truncate">
          {activeBook?.title || "My Story"}
        </span>
        <span className="font-sans text-[10px] sm:text-xs text-[#7A8A7A] flex-shrink-0">
          ({activeBook?.chapter_count || 0})
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-[#7A8A7A] transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setIsOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="absolute top-full right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] bg-white rounded-2xl shadow-xl border border-[#D4C9B8]/40 z-50 overflow-hidden"
              data-testid="book-dropdown"
            >
              <div className="p-3 border-b border-[#D4C9B8]/30">
                <p className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider">Your Memoirs</p>
              </div>

              <div className="max-h-64 overflow-y-auto">
                {books.map((book) => (
                  <div key={book.id} className={`group px-3 py-2.5 flex items-center justify-between hover:bg-[#F8F6F1] transition-colors ${book.id === activeBookId ? "bg-[#9FB89F]/10" : ""}`}>
                    {renamingId === book.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={renameTitle}
                          onChange={(e) => setRenameTitle(e.target.value)}
                          className="flex-1 text-sm font-sans bg-transparent border-b border-[#7A9B7E] outline-none py-0.5"
                          autoFocus
                          onKeyDown={(e) => e.key === "Enter" && handleRename(book.id)}
                          data-testid={`rename-input-${book.id}`}
                        />
                        <button onClick={() => handleRename(book.id)} className="p-1 text-[#7A9B7E]"><Check className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setRenamingId(null)} className="p-1 text-[#7A8A7A]"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : deletingId === book.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-xs text-red-500 font-sans">Delete this memoir?</span>
                        <button onClick={() => handleDelete(book.id)} className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full" data-testid={`confirm-delete-book-${book.id}`}>Yes</button>
                        <button onClick={() => setDeletingId(null)} className="px-2 py-0.5 bg-[#E5D4C1] text-xs rounded-full">No</button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => handleSwitch(book.id)}
                          className="flex items-center gap-2.5 flex-1 text-left min-w-0"
                          data-testid={`book-item-${book.id}`}
                        >
                          {book.id === activeBookId && <div className="w-1.5 h-1.5 rounded-full bg-[#7A9B7E] flex-shrink-0" />}
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className={`font-sans text-sm truncate ${book.id === activeBookId ? "font-medium text-[#2D3A2D]" : "text-[#4A5D4A]"}`}>{book.title}</p>
                              {book.is_occasion && (
                                <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-[#C67B5C]/15 text-[#C67B5C] rounded text-[10px] font-sans font-medium flex-shrink-0">
                                  <Users className="w-2.5 h-2.5" />
                                  Collab
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="font-sans text-xs text-[#7A8A7A]">{book.chapter_count || 0} chapters</p>
                              {book.genre && <span className="font-sans text-xs text-[#9FB89F]">{book.genre}</span>}
                            </div>
                          </div>
                        </button>
                        <div className="flex gap-0.5 items-center flex-shrink-0 ml-1">
                          <button
                            onClick={(e) => handleSuggestTitle(e, book.id)}
                            disabled={suggestingId === book.id || (book.chapter_count || 0) < 1}
                            className="p-1.5 rounded-lg transition-colors text-[#7A9B7E] hover:bg-[#9FB89F]/20 disabled:opacity-30 disabled:cursor-not-allowed"
                            title={(book.chapter_count || 0) < 1 ? "Add at least one chapter first" : "AI: Suggest a new title"}
                            data-testid={`suggest-title-${book.id}`}
                          >
                            <Sparkles className={`w-4 h-4 ${suggestingId === book.id ? "animate-pulse" : ""}`} />
                          </button>
                          <button
                            onClick={(e) => handleSuggestCover(e, book.id)}
                            disabled={coverGenId === book.id || (book.chapter_count || 0) < 1}
                            className="p-1.5 rounded-lg transition-colors text-[#C67B5C] hover:bg-[#C67B5C]/15 disabled:opacity-30 disabled:cursor-not-allowed"
                            title={(book.chapter_count || 0) < 1 ? "Add at least one chapter first" : "AI: Generate a beautiful cover"}
                            data-testid={`suggest-cover-${book.id}`}
                          >
                            <ImageIcon className={`w-4 h-4 ${coverGenId === book.id ? "animate-pulse" : ""}`} />
                          </button>
                          <button
                            onClick={(e) => handleTogglePublic(e, book.id, book.is_public)}
                            className={`p-1.5 rounded-lg transition-colors ${book.is_public ? "text-[#7A9B7E] hover:bg-[#9FB89F]/20" : "text-[#7A8A7A] hover:bg-[#E5D4C1]/50"}`}
                            title={book.is_public ? "Public — click to make private" : "Private — click to make public"}
                            data-testid={`toggle-public-${book.id}`}
                          >
                            {book.is_public ? <Globe className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={() => { setRenamingId(book.id); setRenameTitle(book.title); }}
                            className="p-1.5 hover:bg-[#E5D4C1]/50 rounded-lg"
                            data-testid={`rename-book-${book.id}`}
                          >
                            <Edit2 className="w-3.5 h-3.5 text-[#7A8A7A]" />
                          </button>
                          {books.length > 1 && (
                            <button
                              onClick={() => setDeletingId(book.id)}
                              className="p-1.5 hover:bg-red-50 rounded-lg"
                              data-testid={`delete-book-${book.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Create new */}
              <div className="p-3 border-t border-[#D4C9B8]/30">
                {creating ? (
                  <div className="space-y-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      placeholder="Memoir title..."
                      className="w-full text-sm font-sans bg-[#F8F6F1] border border-[#D4C9B8] rounded-lg px-3 py-1.5 outline-none focus:border-[#7A9B7E]"
                      autoFocus
                      onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                      data-testid="new-book-title-input"
                    />
                    <select
                      value={newGenre}
                      onChange={(e) => setNewGenre(e.target.value)}
                      className="w-full text-sm font-sans bg-[#F8F6F1] border border-[#D4C9B8] rounded-lg px-3 py-1.5 outline-none focus:border-[#7A9B7E] text-[#4A5D4A]"
                      data-testid="new-book-genre-select"
                    >
                      {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <select
                      value={newTemplate}
                      onChange={(e) => setNewTemplate(e.target.value)}
                      className="w-full text-sm font-sans bg-[#F8F6F1] border border-[#D4C9B8] rounded-lg px-3 py-1.5 outline-none focus:border-[#7A9B7E] text-[#4A5D4A]"
                      data-testid="new-book-template-select"
                    >
                      <option value="freestyle">Freestyle (No template)</option>
                      <option value="family_history">Family History</option>
                      <option value="travel_journal">Travel Journal</option>
                      <option value="career">Career & Professional</option>
                      <option value="childhood">Childhood Memories</option>
                      <option value="love_story">Love Story</option>
                      <option value="personal_growth">Personal Growth</option>
                      <option value="adventure">Adventure & Exploration</option>
                      <option value="immigration">Immigration Story</option>
                      <option value="war_service">War & Service</option>
                    </select>
                    <div className="flex gap-2">
                      <button onClick={handleCreate} className="flex-1 py-1.5 bg-[#7A9B7E] text-white rounded-lg text-sm font-sans" data-testid="create-book-confirm">Create</button>
                      <button onClick={() => { setCreating(false); setNewTitle(""); setNewTemplate("freestyle"); }} className="flex-1 py-1.5 bg-[#E5D4C1] text-[#6A5A4A] rounded-lg text-sm font-sans">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      if (onRequestCreate) {
                        setIsOpen(false);
                        onRequestCreate();
                      } else {
                        setCreating(true);
                      }
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-sans text-[#7A9B7E] hover:bg-[#9FB89F]/10 transition-colors"
                    data-testid="create-new-book-btn"
                  >
                    <Plus className="w-4 h-4" />
                    New Memoir
                  </button>
                )}
              </div>

              {/* Mobile-only "More options" — Style + Events (hidden on sm+ since they live in the navbar there) */}
              {(onWritingStyleChange || onOpenOccasions) && (
                <div className="sm:hidden p-3 border-t border-[#D4C9B8]/30 bg-[#F8F6F1]/40 space-y-2.5" data-testid="mobile-more-options">
                  <p className="font-sans text-[10px] text-[#7A8A7A] uppercase tracking-wider mb-1">More options</p>

                  {onWritingStyleChange && (
                    <label className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-[#D4C9B8]/50">
                      <span className="flex items-center gap-2 font-sans text-xs text-[#4A5D4A]">
                        <Pen className="w-3.5 h-3.5 text-[#7A9B7E]" />
                        Writing style
                      </span>
                      <select
                        value={writingStyle || "standard"}
                        onChange={(e) => onWritingStyleChange(e.target.value)}
                        className="text-[11px] font-sans bg-transparent text-[#2D3A2D] outline-none"
                        data-testid="mobile-writing-style-select"
                      >
                        <option value="standard">Standard</option>
                        <option value="simple">Simple English</option>
                        <option value="humorous">Humorous</option>
                      </select>
                    </label>
                  )}

                  {onOpenOccasions && (
                    <button
                      onClick={() => { setIsOpen(false); onOpenOccasions(); }}
                      className="flex items-center justify-between w-full gap-2 px-2.5 py-2 rounded-lg bg-white border border-[#D4C9B8]/50 hover:bg-[#F8F6F1] transition-colors"
                      data-testid="mobile-events-shortcut"
                    >
                      <span className="flex items-center gap-2 font-sans text-xs text-[#4A5D4A]">
                        <Users className="w-3.5 h-3.5 text-[#7A9B7E]" />
                        Events &amp; collaborations
                      </span>
                      <ChevronDown className="w-3.5 h-3.5 -rotate-90 text-[#7A8A7A]" />
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookSelector;
